import { Folder } from '@mui/icons-material'
import { Tooltip } from '@mui/material'
import { abbreviateNumber } from 'js-abbreviation-number'
import { timeAgo } from '@utils/timeAgo'
import { Integration } from 'pages/datasets/types'
import { Integrations } from './integrations'
import * as css from './styles'
import { t } from '@lingui/core/macro'
import { Link } from 'react-router-dom'
import { integrationSlugs } from '@global/constants'
import { useSelector } from 'react-redux'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'

interface IProps {
  folderName: string | undefined
  rowCount: number | null
  integrations: Integration[]
  lastUpdated: string
  id: number
}

/**
 * A meta detail UI for the dataset preview card.
 * -------------------------------------------------
 * Folder Name  |  12k Rows  |  (x))))))  |  3hr ago
 * -------------------------------------------------
 * @param props
 * @param {string} props.folderName The folder name of a dataset.
 * @param {number} props.rowCount The number of rows in a dataset.
 * @param {Integration[]} props.integrations The dataset integrations.
 * @param {Date} props.lastUpdated The last updated date.
 * @param {number} props.id ID of template.
 */
export const Meta = ({ folderName, rowCount, integrations = [], lastUpdated, id }: IProps) => {
  const timeAgoDMini = timeAgo.format(new Date(lastUpdated), 'mini-now')
  const timeAgoFull = timeAgo.format(new Date(lastUpdated))
  const shortTimeAgo = t`${timeAgoDMini} Ago`
  const showRowCountOnceShortened = rowCount && rowCount > 1000

  const [CAMPAIGN_HEALTH_ENABLED = false] = useSelector(
    selectFeatureIncludes([FeatureDict.CAMPAIGN_HEALTH]),
  )
  const isMetaIntegration = integrations.includes(integrationSlugs.FACEBOOK_MARKETING_INBOUND)

  return (
    <css.Meta>
      <Folder fontSize="small" />
      <css.MetaDetail>{folderName}</css.MetaDetail>
      <css.MetaDetailSeparator />
      <Tooltip placement="bottom" title={showRowCountOnceShortened ? rowCount : ''}>
        <css.MetaDetail>
          {rowCount ? abbreviateNumber(rowCount, 1, { padding: false }) : 0} {t`Rows`}
        </css.MetaDetail>
      </Tooltip>
      <css.MetaDetailSeparator />
      {integrations.length > 0 && (
        <>
          <Integrations data={integrations} />
          <css.MetaDetailSeparator />
        </>
      )}
      <Tooltip placement="bottom" title={t`Last Updated ${timeAgoFull}`}>
        <css.MetaDetail>{shortTimeAgo}</css.MetaDetail>
      </Tooltip>
      {CAMPAIGN_HEALTH_ENABLED && isMetaIntegration && id && (
        <>
          <css.MetaDetailSeparator />
          <Link
            to={`/template/${String(id)}/campaign-health`}
            style={{ color: 'hsla(206, 100%, 50%, 1)' }}
            onClick={e => {
              e.stopPropagation()
            }}
          >
            {t`View campaign health`}
          </Link>
        </>
      )}
    </css.Meta>
  )
}
